import { useState } from "react";
import Divider from '@mui/material/Divider';
import Backdrop from '@mui/material/Backdrop';
import LinearProgressWithLabel from '../components/linear-progress-with-label';
import TextMetadataElement from '../components/text-metadata-element';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import { useDialogs } from '@toolpad/core/useDialogs';
import Grid from '@mui/material/Grid2';
import { useNotifications } from '@toolpad/core/useNotifications';
import {
	useForm,
} from 'react-hook-form'
import {
	TextareaAutosizeElement,
	TextFieldElement,
} from 'react-hook-form-mui'
import FilePicker from '../components/file-picker'
import { useAuth } from 'oidc-react';
import Constants from '../config/constants';
import { hexToDecimal } from '../utils/color-util';
import { dataToChunks, generateShortCode } from '../utils/util';
import { handleZipDownload, handleCsvDownload, resolveZipName, resolveCsvName } from '../utils/file-util';
import { CertificateService } from '../services/certificate-service';


function CertificateBasicPageContent({ pathname }) {
	const auth = useAuth();
	const dialogs = useDialogs();

	const [imageUploaded, setImageUploaded] = useState(false);
	const [showLoader, setShowLoader] = useState(false);
	const [progress, setProgress] = useState(0);

	const service = new CertificateService(auth);
	const notifications = useNotifications();


	const { control, handleSubmit, setValue, getValues } = useForm({
		defaultValues: {
			nameTemplate: 'CFA_certificate_{name}_{code}.jpg',
			names: '',
			element: {
				text: null,
				xOffset: 0,
				yOffset: -200,
				font: Constants.DEFAULT_FONT,
				color: hexToDecimal(Constants.DEFAULT_FONT_COLOR),
				fontSize: 140,
				style: 144,
			}
		},
	});

	const onImageUpload = (data) => {
		setValue('template', data.content);
		setValue('contentType', data.contentType)

		setImageUploaded(true);
	};

	const onTextElementUpdate = (element) => {
		setValue('element.xOffset', element.xOffset);
		setValue('element.yOffset', element.yOffset);
		setValue('element.font', element.font);
		setValue('element.color', hexToDecimal(element.color.hex));
		setValue('element.fontSize', element.fontSize);
		setValue('element.style', element.style);
	};

	const submit = async (data) => {
		try {
			setShowLoader(true);
			let operationsCompleted = 0;

			const payloads = buildPayloadChunks(data)
				.map(p => service.createMany(p)
					.finally(() => increaseAndSet()));

			increaseAndSet();

			const results = await Promise
				.all(payloads);

			setShowLoader(false);
			handleZipDownload(results, resolveZipName(data.nameTemplate));

			notifications.show('Certificates generated successfully.', Constants.NOTIFICATION_SUCCESS);

			function increaseAndSet() {
				setProgress(Math.min(100, Math.round(100 * (operationsCompleted++ / payloads.length))));
			}
		} catch (e) {
			notifications.show('Could not generate certificates. ' + e, Constants.NOTIFICATION_ERROR);
		} finally {
			setShowLoader(false);
		}
	};

	const handleUpload = async () => {
		const projectShortCode = await dialogs.prompt('Please specify the project short-code', {
			okText: 'Upload',
			cancelText: 'Cancel',
		});

		if (!projectShortCode) {
			return;
		}

		const data = getValues();

		try {
			setShowLoader(true);
			let operationsCompleted = 0;

			const payloads = buildPayloadChunks(data)
				.map(c => { return { ...c, urlPrefix: projectShortCode } })
				.map(p => service.uploadMany(p)
					.finally(() => increaseAndSet()));

			increaseAndSet();

			const results = (await Promise
				.all(payloads))
				.flatMap(r => r);

			handleCsvDownload(results, resolveCsvName(data.nameTemplate));

			setShowLoader(false);

			notifications.show('Certificates uploaded successfully.', Constants.NOTIFICATION_SUCCESS);

			function increaseAndSet() {
				setProgress(Math.min(100, Math.round(100 * (operationsCompleted++ / payloads.length))));
			}
		} catch (e) {
			notifications.show('Could not upload certificates. ' + e, Constants.NOTIFICATION_ERROR);
		} finally {
			setShowLoader(false);
		}

	};

	const buildPayloadChunks = (data) => {

		return dataToChunks(data.names
			.split("\n"))
			.map(c => {
				return {
					template: {
						content: data.template,
						contentType: data.contentType,
					},
					images: c
						.filter(n => n)
						.map(n => {
							return {
								artistName: n,
								filename: data.nameTemplate
									.replace("{name}", n)
									.replace("{code}", generateShortCode()),
								elements: [{
									class: Constants.ELEMENT_TEXT,
									text: n,
									xOffset: data.element.xOffset,
									yOffset: data.element.yOffset,
									font: data.element.font,
									color: data.element.color,
									fontSize: data.element.fontSize,
									style: data.element.style
								}]
							}
						}),
				};
			});
	};

	return (
		<>
			<Divider sx={{ m: 2 }} />
			<Backdrop sx={(theme) => ({ color: '#fff', zIndex: theme.zIndex.drawer + 1 })} open={showLoader} >
				<LinearProgressWithLabel value={progress} />
			</Backdrop>
			<form onSubmit={handleSubmit(submit)} noValidate>
				<Grid container spacing={4} padding={2} columns={{ xs: 2, sm: 6, md: 6, xl: 12 }}>
					<Grid size={8}>
						<FilePicker onUploadImage={onImageUpload} />
					</Grid>
					<Grid size={4}>
						<TextareaAutosizeElement name={'names'} label={'Names'} rows={22} control={control} required fullWidth />
					</Grid>

					<Grid size={12}>
						<TextFieldElement name={'nameTemplate'} label={'Name Template'} control={control} required fullWidth />
					</Grid>

					<Grid size={12}>
						<TextMetadataElement
							style={Constants.STYLE_ALIGN_CENTER | Constants.STYLE_VERTICAL_ALIGN_CENTER}
							onUpdate={onTextElementUpdate}
						/>
					</Grid>

					<Grid size={6}>
						<Button type={'submit'} color={'primary'} disabled={!imageUploaded}>
							Download
                        </Button>
					</Grid>
					<Grid size={6}>
						<LoadingButton variant="contained" disabled={!imageUploaded} onClick={handleUpload}>
							Upload
						</LoadingButton>
					</Grid>
				</Grid>
			</form>
		</>
	);
}

export default CertificateBasicPageContent;